import { FC, ReactNode } from 'react';
import { Switch } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { Button, buttonType } from '../../../../../components/button/button';
import { Input, inputType } from '../../../../../components/input/input';
import { digitSperator, faNumToEnNum } from '../../../../../core/utils/decimalExtentions';

/** A titled settings block with a responsive field grid and a save action row. */
export const SettingsSection: FC<{
    title?: string;
    description?: string;
    children: ReactNode;
    onSave: () => void;
    loading?: boolean;
    saveText?: string;
    disabled?: boolean;
}> = ({ title, description, children, onSave, loading, saveText, disabled }) => (
    <div className="settings-section">
        {(title || description) && (
            <div className="settings-section__head">
                {title && <h3>{title}</h3>}
                {description && <p>{description}</p>}
            </div>
        )}
        <div className="settings-grid">{children}</div>
        <div className="settings-section__actions">
            <Button
                type={buttonType.info}
                htmlType="button"
                text={saveText ?? 'ذخیره تغییرات'}
                onClick={onSave}
                loading={loading}
                disabled={loading || disabled}
            />
        </div>
    </div>
);

/** Numeric input that sanitizes Persian digits and (optionally) shows thousands separators. */
export const NumberInput: FC<{
    label: string;
    value: string | number | undefined | null;
    onChange: (value: string) => void;
    placeholder?: string;
    allowDecimal?: boolean;
    separated?: boolean;
    suffix?: ReactNode;
    disabled?: boolean;
}> = ({ label, value, onChange, placeholder, allowDecimal = false, separated = false, suffix, disabled }) => {
    const raw = value === undefined || value === null ? '' : String(value);
    const display = separated ? digitSperator(raw) : raw;

    const handle = (input: string) => {
        let v = faNumToEnNum(input ?? '').replace(/,/g, '');
        v = allowDecimal ? v.replace(/[^0-9.]/g, '') : v.replace(/[^0-9]/g, '');
        if (allowDecimal) {
            const parts = v.split('.');
            if (parts.length > 2) v = `${parts[0]}.${parts.slice(1).join('')}`;
        }
        onChange(v);
    };

    return (
        <Input
            type={inputType.text}
            label={label}
            name={label}
            placeholder={placeholder ?? label}
            value={display}
            suffix={suffix}
            dir="ltr"
            disabled={disabled}
            onChange={(e) => handle(e.target.value)}
        />
    );
};

/** Plain text input (LTR by default — most credentials/keys are latin). */
export const TextInput: FC<{
    label: string;
    value: string | undefined | null;
    onChange: (value: string) => void;
    placeholder?: string;
    dir?: 'ltr' | 'rtl';
    maxLength?: number;
    disabled?: boolean;
    htmlType?: string;
}> = ({ label, value, onChange, placeholder, dir = 'ltr', maxLength, disabled, htmlType }) => (
    <Input
        type={inputType.text}
        label={label}
        name={label}
        placeholder={placeholder ?? label}
        value={value ?? ''}
        dir={dir}
        maxLength={maxLength}
        disabled={disabled}
        htmlType={htmlType}
        onChange={(e) => onChange(e.target.value)}
    />
);

/** Labeled on/off switch used across settings tabs. */
export const SwitchField: FC<{
    label: string;
    checked: boolean | undefined;
    onChange: (checked: boolean) => void;
    hint?: string;
    disabled?: boolean;
}> = ({ label, checked, onChange, hint, disabled }) => (
    <div className="settings-switch">
        <div className="settings-switch__text">
            <label>{label}</label>
            {hint && <span className="settings-switch__hint">{hint}</span>}
        </div>
        <Switch
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
            checked={!!checked}
            disabled={disabled}
            onChange={onChange}
        />
    </div>
);
